<script setup lang="ts">
import HelpCenterTriggerButton from '@/modules/HelpCenter/HelpCenterTriggerButton.vue'
import { useChangelogStore } from '@/modules/HelpCenter/useChangelogStore'
import { ANALYTICS_EVENT, useAnalytics } from '@/sharedComposables/useAnalytics'
import DividerLine from '@/uiKit/DividerLine.vue'
import IconButton from '@/uiKit/IconButton.vue'
import ModalDialog from '@/uiKit/ModalDialog.vue'
import { storeToRefs } from 'pinia'
import { onMounted, ref } from 'vue'
import HelpCenterChatWithUs from './HelpCenterChatWithUs.vue'
import HelpCenterSidebarChangelogs from './HelpCenterSidebarChangelogs.vue'
import HelpCenterSidebarTutorials from './HelpCenterSidebarTutorials.vue'

const { captureAnalyticsEvent } = useAnalytics()
const changelogStore = useChangelogStore()
const { hasNewChangelogItemsSinceLastSeen, lastChangelogList } = storeToRefs(changelogStore)

/** For a11y - HTML ID for the dialog's label */
const DIALOG_LABEL_ID = 'help-center-dialog-label'

// we need to check if a tutorial dialog is currently open, to avoid closing
// the main sidebar on click outside
const isNestedDialogOpen = ref(false)
const isOpen = ref(false)

const toggleHelpCenter = () => {
  if (!isOpen.value) {
    captureAnalyticsEvent(ANALYTICS_EVENT.HELP_CENTER_OPENED)

    changelogStore.recordChangelogItemMostRecentDateSeen()
  }

  isOpen.value = !isOpen.value
}

const onClose = () => {
  if (isNestedDialogOpen.value) {
    isNestedDialogOpen.value = false
    return
  }

  isOpen.value = false
}

onMounted(() => changelogStore.fetchChangelog())
</script>

<template>
  <HelpCenterTriggerButton
    :show-badge="hasNewChangelogItemsSinceLastSeen"
    @click="toggleHelpCenter()"
  />
  <ModalDialog
    id="help-center-dialog"
    :open="isOpen"
    placement="right"
    class="flex w-[352px] flex-col gap-2 !rounded-corner-12 bg-surface-primary px-2 py-5 pb-0 shadow-lg outline outline-border-subtle"
    role="dialog"
    :aria-labelledby="DIALOG_LABEL_ID"
    @close="onClose"
  >
    <IconButton
      size="md"
      variant="transparent"
      class="absolute right-2 top-2 text-text-subtle"
      icon="close"
      aria-label="Close"
      rounded
      role="button"
      @click="onClose"
      @mousedown.prevent
    />

    <!-- Header -->
    <div class="flex flex-col px-3">
      <p
        :id="DIALOG_LABEL_ID"
        class="text-lg-15px-bold text-text"
      >
        How can we help?
      </p>
      <p class="text-sm-12px-light text-text-subtlest">Find out help in our learning center.</p>
    </div>

    <!-- Carousel -->
    <HelpCenterChatWithUs />

    <DividerLine
      class="p-2 pb-3"
      color="subtle"
      :height="1"
    />

    <div class="no-scrollbar flex w-[352px] flex-col gap-2 overflow-y-auto">
      <HelpCenterSidebarChangelogs
        :changelogs="lastChangelogList"
        @click="changelogStore.markChangelogReadStatus($event, true)"
      />

      <DividerLine
        class="p-2"
        color="subtle"
        :width="1"
      />
      <HelpCenterSidebarTutorials
        class="pb-5"
        :is-open="isOpen"
        @update-nested-dialog="isNestedDialogOpen = $event"
      />
    </div>
  </ModalDialog>
</template>
